import { Toast } from '@components/Toast';
import { faCloudExclamation, faListUl, faPlusCircle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UpdateSegment } from '@partful/entities/instructions/segment';
import { useCreateSegment } from '@partful/features/instructions/segments/lib/useCreateSegment';
import { useListSegments } from '@partful/features/instructions/segments/lib/useListSegments';
import { ErrorMessage } from '@partful/features/instructions/ui/ErrorMessage';
import { VersionModel } from '@partful/instructions-domain-models';
import { Button } from '@partful/shared/ui/Button';
import { LoadingSpinner } from '@partful/shared/ui/LoadingSpinner/LoadingSpinner';
import { Typography } from '@partful/shared/ui/Typography';
import { useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { SegmentForm } from '../SegmentForm/SegmentForm';
import { SegmentsListItem } from '../SegmentListItem/SegmentListItem';
import styles from './SegmentsList.module.scss';

type SegmentsListProps = {
  version: VersionModel;
  languageCode: string;
};

export function SegmentsList({ version, languageCode }: Readonly<SegmentsListProps>) {
  const { t: i18n } = useTranslation();
  const { data: segments, isLoading, isError } = useListSegments(version.instructionId, version.id, languageCode);

  const {
    mutateAsync: createSegment,
    isLoading: isCreatingSegment,
    isError: isErrorCreatingSegment,
    isSuccess: isSuccessCreatingSegment,
  } = useCreateSegment();

  const [isCreating, setIsCreating] = useState(false);

  const handleCreateSegment: SubmitHandler<UpdateSegment> = async ({ name, description }) => {
    try {
      await createSegment({
        instructionId: version.instructionId,
        versionId: version.id,
        args: {
          order: segments!.length,
          content: {
            languageCode,
            name,
            description: description!,
          },
        },
      });
      setIsCreating(false);
    } catch (e) {
      // Don't need to handle this here
    }
  };

  if (isLoading) {
    return <LoadingSpinner className={styles.loader} />;
  }

  if (isError) {
    return (
      <div className={styles.error}>
        <ErrorMessage
          icon={<FontAwesomeIcon icon={faCloudExclamation} />}
          title={i18n('instructions:manage.version.segments.loadFailed.title')}
          message={i18n('instructions:manage.version.segments.loadFailed.message')}
        />
      </div>
    );
  }

  return (
    <div className={styles.segments}>
      <div className={styles.segmentsList} data-testid="segments-list">
        {segments?.length === 0 && (
          <div className={styles.noSegments}>
            <FontAwesomeIcon icon={faListUl} />
            <Typography>{i18n('instructions:manage.version.segments.noSegments')}</Typography>
          </div>
        )}

        {segments?.map((segment) => (
          <SegmentsListItem key={segment.id} version={version} segment={segment} languageCode={languageCode} />
        ))}
      </div>
      {isCreating && (
        <SegmentForm
          className={styles.createForm}
          onSubmit={handleCreateSegment}
          onCancel={() => setIsCreating(false)}
          disabled={isCreatingSegment}
        />
      )}
      {!isCreating && (
        <Button
          name="instructions_version_langandinstructions_create_segment_click"
          size="large"
          onClick={() => setIsCreating(true)}
          className={styles.createSegmentButton}
        >
          <FontAwesomeIcon icon={faPlusCircle} />
          {i18n('instructions:manage.version.segments.create')}
        </Button>
      )}
      <Toast
        dependency={isErrorCreatingSegment}
        severity="error"
        title={i18n('instructions:manage.version.segments.createError.title')}
        message={i18n('instructions:manage.version.segments.createError.message')}
        testId="create-segment-error-message"
      />
      <Toast
        dependency={isSuccessCreatingSegment}
        severity="success"
        title={i18n('instructions:manage.version.segments.createSuccess.title')}
        testId="create-segment-success-message"
      />
    </div>
  );
}
